/* eslint-disable max-len */
import React from 'react'
import styled from 'styled-components'
import {useManageScheme} from './manageSchemeProvider'

const Icon = styled.svg`
  font-size: ${(props) => props.fontSize};
  color: ${(props) => props.color};
`

export const LogoColors = {
  black: '#0B0B0B',
  red: '#FF5500',
  white: '#FFFFFF',
} as const

interface LogoProps {
  className?: string
  color?: keyof typeof LogoColors
  size?: string
}

export const SanityScribble = ({color, size, ...props}: LogoProps) => {
  const {resolvedScheme} = useManageScheme()

  const resolvedColor = color || (resolvedScheme === 'dark' ? 'white' : 'black')

  const backgroundColor = LogoColors[resolvedColor]
  const scribbleColor = resolvedColor === 'black' ? LogoColors['white'] : LogoColors['black']

  return (
    <Icon
      {...props}
      viewBox="0 0 256 256"
      xmlns="http://www.w3.org/2000/svg"
      width={size || '1em'}
      fill="none"
    >
      <g clip-path="url(#clip0_2_1553)">
        <rect width="256" height="256" fill={backgroundColor} />
        <rect width="256" height="256" fill={backgroundColor} />
        <g clip-path="url(#clip1_2_1553)">
          <path
            d="M215.759 152.483L208.799 140.366L175.13 160.88L212.526 113.252L218.179 109.933L216.78 107.831L219.349 104.548L207.549 94.7227L202.147 101.608L93.1263 165.414L133.434 116.925L208.512 75.7566L201.379 61.963L160.486 84.3775L180.623 60.168L169.087 50L123.767 104.513L78.7575 129.206L113.217 83.6335L134.811 72.3909L127.953 58.4438L65.0424 91.2034L82.1978 68.4937L70.2143 58.8926L34 106.839L34.5619 107.288L41.3277 121.07L81.4753 100.155L44.8826 148.539L50.8801 153.345L54.4465 160.242L96.7156 137.06L50.1691 193.061L61.7054 203.229L64.0218 200.442L176.311 134.509L139.031 182.007L139.638 182.515L139.581 182.55L147.31 196.001L196.895 165.781L177.802 196.603L190.6 205L221 155.931L215.759 152.483Z"
            fill={scribbleColor}
          />
        </g>
      </g>
      <defs>
        <clipPath id="clip0_2_1553">
          <rect width="256" height="256" fill={scribbleColor} />
        </clipPath>
        <clipPath id="clip1_2_1553">
          <rect width="187" height="155" fill={scribbleColor} transform="translate(34 50)" />
        </clipPath>
      </defs>
    </Icon>
  )
}

const SanityLogo = ({className, size, color}: LogoProps) => {
  return (
    <Icon
      // eslint-disable-next-line react/prop-types
      className={className}
      color={color ? LogoColors[color] : undefined}
      viewBox="0 0 435 154"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      height={size || '1.5em'}
    >
      <title>Sanity</title>

      <g clipPath="url(#clip0_2001_20)">
        <path
          d="M44.883 52.4463C33.4181 43.7971 21.8121 37.102 21.8121 27.338C21.8121 21.482 26.9827 15.4851 34.1181 15.4851C46.983 15.4851 54.5302 28.8754 61.942 45.6103H66.5538V7.11176H34.3946C7.82952 7.11176 0 23.1543 0 35.7055C0 52.1646 13.5649 61.9286 28.9416 73.3649C39.7064 81.3158 48.7948 88.7092 48.7948 97.2174C48.7948 106.424 42.5006 111.723 34.5299 111.723C26.0004 111.723 13.4178 100.562 4.60595 77.4137H0V120.096H36.777C59.4302 120.096 70.7539 102.24 71.1716 87.8759C71.7304 71.141 57.3302 61.7936 44.883 52.4463Z"
          fill="currentColor"
        />
        <path
          d="M145.002 106.841V61.7878C145.002 42.2598 132.837 37.102 115.219 37.102H83.8953L84.0364 68.9054H89.2071C94.6601 55.3743 102.49 45.4753 110.878 45.4753C118.149 45.4753 121.366 51.7538 121.366 58.3082V64.4459C109.619 71.4227 78.3011 77.2787 78.3011 98.479C78.3011 110.754 86.8306 120.935 99.2778 120.935C110.043 120.935 117.455 113.958 121.231 105.732C121.931 112.568 126.402 120.096 136.614 120.096H152.696V113.958C147.526 113.958 145.008 110.473 145.008 106.846L145.002 106.841ZM120.949 99.8638C118.713 103.073 115.213 106.7 111.019 106.7C105.148 106.7 101.372 102.234 101.372 93.3095C101.372 83.1289 115.072 76.5746 120.949 71.6926V99.8697V99.8638Z"
          fill="currentColor"
        />
        <path
          d="M270.145 28.4529C279.092 28.4529 284.686 22.1744 284.686 14.0885C284.686 6.00274 279.092 0 270.145 0C261.198 0 255.462 5.99688 255.462 14.0885C255.462 22.1802 261.474 28.4529 270.145 28.4529Z"
          fill="currentColor"
        />
        <path
          d="M409.135 37.102V43.2397C417.947 43.2397 420.459 47.84 416.129 60.2563L405.641 88.5683L392.217 54.1186C389.141 47.4234 390.817 43.2397 397.252 43.2397V37.102H330.557V9.20654H324.263C322.446 18.6889 312.516 37.102 295.598 37.102V43.2397H306.504V100.568C306.504 110.191 309.722 121.07 328.457 121.07H357.54V89.5482H352.369C350.269 96.9417 345.94 111.723 336.569 111.723C331.399 111.723 330.557 106.002 330.557 101.401V45.8861H357.264C360.481 45.8861 363.834 46.3027 365.511 50.2106L393.894 120.231C388.441 132.647 379.07 134.595 362.293 129.437V153.988C366.628 153.988 377.811 154.129 379.77 153.572C390.817 150.362 398.364 128.745 401.441 120.378L425.488 55.521C428.147 48.4092 430.382 43.2456 434.994 43.2456V37.1079H409.129L409.135 37.102Z"
          fill="currentColor"
        />
        <path
          d="M237.703 106.841V59.2764C237.703 44.3546 230.574 35.4238 216.45 35.4238C204.009 35.4238 196.803 44.0319 191.279 52.0414V37.0961H159.396V43.2338C164.849 43.2338 167.226 46.5785 167.226 50.4864V106.835C167.226 110.602 164.432 113.947 159.396 113.947V120.084H199.109V113.947C194.073 113.947 191.279 110.602 191.279 106.835V59.646C194.503 55.3743 198.214 50.7681 204.42 50.7681C210.291 50.7681 213.65 55.5093 213.65 61.2303V106.841C213.65 110.608 210.856 113.952 205.82 113.952V120.09H245.533V113.952C240.497 113.952 237.703 110.608 237.703 106.841Z"
          fill="currentColor"
        />
        <path
          d="M284.127 106.841V37.102H252.245V43.2397C257.698 43.2397 260.074 46.5843 260.074 50.4923V106.841C260.074 110.608 257.28 113.952 252.245 113.952V120.09H291.957V113.952C286.922 113.952 284.127 110.608 284.127 106.841Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_2001_20">
          <rect width="435" height="154" fill="white" />
        </clipPath>
      </defs>
    </Icon>
  )
}

export default SanityLogo
